import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { adminApi } from '../lib/api';
import { Tag, Plus, Trash2, Percent, Clock, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const typeInfo = {
    percentage: { icon: Percent, label: 'Percentage Discount', color: 'text-blue-400' },
    fixed_amount: { icon: DollarSign, label: 'Fixed Amount Off', color: 'text-emerald-400' },
    free_trial: { icon: Clock, label: 'Free Trial', color: 'text-purple-400' },
};

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ code: '', coupon_type: 'percentage', discount_value: '', description: '', max_uses: '', is_active: true });

    const loadCoupons = () => {
        setLoading(true);
        adminApi.getCoupons().then(({ data }) => setCoupons(data)).catch(() => toast.error('Failed to load coupons')).finally(() => setLoading(false));
    };

    useEffect(() => { loadCoupons(); }, []);

    const handleCreate = async () => {
        if (!form.code || !form.discount_value) { toast.error('Code and value are required'); return; }
        try {
            await adminApi.createCoupon({
                code: form.code, coupon_type: form.coupon_type, discount_value: parseFloat(form.discount_value),
                description: form.description || null, max_uses: form.max_uses ? parseInt(form.max_uses) : null, is_active: true,
            });
            toast.success('Coupon created');
            setShowForm(false);
            setForm({ code: '', coupon_type: 'percentage', discount_value: '', description: '', max_uses: '', is_active: true });
            loadCoupons();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create coupon');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this coupon?')) return;
        try { await adminApi.deleteCoupon(id); toast.success('Deleted'); loadCoupons(); }
        catch { toast.error('Failed to delete'); }
    };

    return (
        <Layout>
            <div data-testid="admin-coupons-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100">Coupons</h1>
                        <p className="text-slate-400 text-sm mt-1">Manage discount codes and free trials</p>
                    </div>
                    <Button onClick={() => setShowForm(!showForm)} className="brand-btn font-medium" data-testid="create-coupon-button">
                        <Plus className="h-4 w-4 mr-1" /> New Coupon
                    </Button>
                </div>

                {showForm && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 mb-6" data-testid="coupon-form">
                        <h2 className="text-lg font-semibold text-slate-200 mb-4">Create Coupon</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-slate-300">Code *</Label>
                                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. WELCOME50" data-testid="coupon-code-input" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Type</Label>
                                <select value={form.coupon_type} onChange={(e) => setForm({ ...form, coupon_type: e.target.value })}
                                    className="mt-1 w-full h-10 rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" data-testid="coupon-type-select">
                                    <option value="percentage">Percentage Discount</option>
                                    <option value="fixed_amount">Fixed Amount Off (£)</option>
                                    <option value="free_trial">Free Trial (Days)</option>
                                </select>
                            </div>
                            <div>
                                <Label className="text-slate-300">
                                    {form.coupon_type === 'percentage' ? 'Discount %' : form.coupon_type === 'fixed_amount' ? 'Amount (£)' : 'Trial Days'} *
                                </Label>
                                <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="e.g. 50" data-testid="coupon-value-input" />
                            </div>
                            <div>
                                <Label className="text-slate-300">Max Uses (blank = unlimited)</Label>
                                <Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Leave blank for unlimited" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-slate-300">Description</Label>
                                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100" placeholder="Optional description" />
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button onClick={handleCreate} className="brand-btn font-medium" data-testid="save-coupon-button">Create Coupon</Button>
                            <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancel</Button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="space-y-3">{[1, 2].map(i => <div key={i} className="h-20 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : coupons.length === 0 ? (
                    <div className="text-center py-16 text-slate-500"><Tag className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No coupons yet</p></div>
                ) : (
                    <div className="space-y-3">
                        {coupons.map((c) => {
                            const info = typeInfo[c.coupon_type] || typeInfo.percentage;
                            const Icon = info.icon;
                            return (
                                <div key={c.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`coupon-card-${c.id}`}>
                                    <div className={`p-2 rounded-lg ${info.color} bg-current/10 shrink-0`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-base font-bold text-slate-100 font-mono">{c.code}</span>
                                            {!c.is_active && <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-400 rounded-full">Inactive</span>}
                                        </div>
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {info.label}: <span className="text-slate-300">{c.discount_value}{c.coupon_type === 'percentage' ? '%' : c.coupon_type === 'free_trial' ? ' days' : ' GBP'}</span>
                                            {c.description && ` — ${c.description}`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="text-xs text-slate-500">{c.uses_count || 0}{c.max_uses ? `/${c.max_uses}` : ''} uses</span>
                                        <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-8 w-8 p-0"
                                            onClick={() => handleDelete(c.id)} data-testid={`delete-coupon-${c.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
